import {
  Badge,
  Button,
  LegacyCard,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import { useHistory } from "react-router"

import { ICheckIsActive } from "@/api/freeReturnService/type"
import { Switch, TooltipByIcon } from "@/components"
import { FeatureWidget } from "@/components"
import stores from "@/stores"

// cartPage例子模块
const renderSkeletonByCartPage = () => {
  return (
    <div className="flex gap-6 w-full items-center justify-between">
      <div className="w-[40px] h-[40px] bg-[#E3E3E3] rounded-lg"></div>
      <div className="flex flex-col gap-2">
        <div className="w-[140px] h-[8px] bg-[#E3E3E3] rounded-lg"></div>
        <div className="w-[80px] h-[8px] bg-[#E3E3E3] rounded-lg"></div>
      </div>
      <div className="flex-1 justify-end flex">
        <div className="w-[46px] h-[8px] bg-[#E3E3E3] rounded-lg"></div>
        <div className="mt-6"></div>
      </div>
    </div>
  )
}

// checkoutPage例子模块
const renderSkeletonByCheckoutPage = () => {
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="w-[40%]">
        <SkeletonBodyText lines={1} />
      </div>
      <SkeletonBodyText lines={1} />
      <div className="w-[60%]">
        <SkeletonBodyText lines={1} />
      </div>
    </div>
  )
}

// 底部按钮模块
const renderFooterSetup = () => {
  return (
    <div
      className="flex justify-end p-4"
      style={{ borderTop: "1px solid #E0E0E0" }}
    >
      <Button>{t("Setup")}</Button>
    </div>
  )
}

// 开关卡片模块
const renderExample = (hasFreeReturnServiceLimit, value, isPlus) => {
  const { UserInfoStore } = stores
  return (
    <LegacyCard sectioned>
      <div className="flex justify-between">
        <div className="flex gap-3 items-center">
          <div className="w-[40px] h-[40px]">
            <img
              src={`https://return-cdn.parcelpanel.net/public/insurance/icon1.svg`}
              className="w-full h-full rounded-lg"
              alt="upsell"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="bodySm" as="span" fontWeight="bold">
              {t("FreeReturnServiceByFormatPrice", {
                replace: {
                  price: "2.00",
                  currency: UserInfoStore.data.currency_format,
                },
              })}
            </Text>
            <Text variant="bodySm" as="span">
              {t("UnlockFreeReturnService")}
            </Text>
          </div>
        </div>
        {/* hover 不展示效果 */}
        <div
          style={{
            "--p-color-icon-secondary-hover": "#8A8A8A",
            "--p-color-icon-hover": "var(--p-color-bg-inverse)",
            "--p-color-icon-secondary-active":
              hasFreeReturnServiceLimit && value === 1
                ? "var(--p-color-bg-inverse)"
                : "var(--p-color-icon-secondary)",
          }}
        >
          <Switch
            disabled={!hasFreeReturnServiceLimit || !isPlus}
            checked={hasFreeReturnServiceLimit && value === 1}
            onChange={(value) => {
              console.log(value)
            }}
          />
        </div>
      </div>
    </LegacyCard>
  )
}

export const ExampleNewCard = observer(
  (props: { cart_insurance_enable: number; hideBadge?: boolean }) => {
    const { CommonStore } = stores
    const hasFreeReturnServiceLimit =
      CommonStore.subscribeBenefitList?.free_return_service
    const { cart_insurance_enable, hideBadge = false } = props
    return (
      <div className="space-y-4">
        <div className="flex gap-2 w-full">
          <Text variant="bodyMd" as="span" fontWeight="bold">
            {t("CartPageWidget")}
          </Text>
          {!hideBadge && (
            <Badge tone={cart_insurance_enable === 1 ? "success" : "enabled"}>
              {cart_insurance_enable === 1 ? t("Published") : t("Unpublished")}
            </Badge>
          )}
        </div>
        <div>{renderSkeletonByCartPage()}</div>
        <div>{renderSkeletonByCartPage()}</div>
        <div>
          {renderExample(
            hasFreeReturnServiceLimit,
            cart_insurance_enable,
            true,
          )}
        </div>
      </div>
    )
  },
)

// 需求：点击整个卡片跳转FreeReturnservice编辑页
const ServiceWidget = ({
  cart_insurance_enable,
  checkout_insurance_enable,
}: ICheckIsActive) => {
  const { UserInfoStore, CommonStore } = stores
  const hasFreeReturnServiceLimit =
    CommonStore.subscribeBenefitList?.free_return_service
  const { push } = useHistory()
  const isPlus = UserInfoStore.data.display_name
    ?.toLowerCase()
    ?.includes("plus")
  return (
    <>
      <LegacyCard>
        <LegacyCard.Section>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Text variant="headingMd" as="h2">
                {t("FreeReturnServiceWidget")}
              </Text>
              <FeatureWidget benefits="free_return_service" />
            </div>
            <div className="flex justify-between items-center gap-4 flex-wrap">
              <div
                className={classNames(
                  "flex justify-between items-center gap-4 flex-1 cursor-pointer basis-80",
                )}
                style={{ "--p-border-radius-300": "8px" }}
                onClick={(e) => {
                  e.stopPropagation()
                  push("/free-return-service/configuration/cart-page")
                }}
              >
                <div className="border border-gray-200 rounded-md border-solid w-full">
                  <div className="p-4">
                    <ExampleNewCard
                      cart_insurance_enable={cart_insurance_enable}
                    />
                  </div>
                  <div>{renderFooterSetup()}</div>
                </div>
              </div>
              <div
                className={classNames(
                  "flex justify-between items-center gap-4 flex-1 basis-80",
                  {
                    "cursor-pointer": isPlus,
                  },
                )}
                style={{ "--p-border-radius-300": "8px" }}
                onClick={(e) => {
                  e.stopPropagation()
                  // 不是plus商家、无法跳转
                  if (!isPlus) return
                  push("/free-return-service/configuration/checkout-page")
                }}
              >
                <div className="border border-gray-200 rounded-md  border-solid w-full">
                  <div>
                    <div className="flex flex-col p-4 gap-4">
                      <div className="flex gap-2 w-full">
                        <Text variant="bodyMd" as="span" fontWeight="bold">
                          {t("CheckoutPageWidget")}
                        </Text>
                        <Badge
                          tone={
                            checkout_insurance_enable === 1
                              ? "success"
                              : "enabled"
                          }
                        >
                          {checkout_insurance_enable === 1
                            ? t("Published")
                            : t("Unpublished")}
                        </Badge>
                      </div>
                      <div>{renderSkeletonByCheckoutPage()}</div>
                      <div>
                        {renderExample(
                          hasFreeReturnServiceLimit,
                          checkout_insurance_enable,
                          isPlus,
                        )}
                      </div>
                      <div>{renderSkeletonByCheckoutPage()}</div>
                    </div>
                    <div
                      className="flex justify-end items-center p-4"
                      style={{ borderTop: "1px solid #E0E0E0" }}
                    >
                      <Button disabled={!isPlus}>{t("Setup")}</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LegacyCard.Section>
      </LegacyCard>
    </>
  )
}

export default observer(ServiceWidget)
